import React from 'react';
import { APITypes, MedicalDataTypes } from '../../../primary-record-types';
import { isGroupedResource } from '../../../hooks/api/useMedicalProfileData';
import { useResourceReferenceCounts } from '../../../hooks/api/useResourceReferenceCounts';
import SkeletonLoadingContainer from '../../v2BaseComponents/SkeletonLoadingContainer';
import * as Card from '../../v2BaseComponents/Card';
import IsInViewport from '../../v2BaseComponents/IsInViewport';

export type Resource = {
    resource: APITypes.medicalDataService.ListResourceResponseItem<MedicalDataTypes.PrimaryRecordResource>;
};

export type ResourceId = {
    resourceId: string;
};

export type LazyLoadedReferenceCountsOwnProps = {
    medicalProfileId: string;
} & (Resource | ResourceId);

export type LazyLoadedReferenceCountsProps = LazyLoadedReferenceCountsOwnProps;

type DataContainerChildren = (args: {
    isLoading: boolean;
    referenceCounts: APITypes.sharedTypes.EntityReferences;
}) => React.ReactNode;

type DataContainerProps = LazyLoadedReferenceCountsProps & { children: DataContainerChildren; isLoadDisabled: boolean };

const DataContainer = (props: DataContainerProps) => {
    const resourceIds = React.useMemo(() => {
        if ('resourceId' in props) {
            return [props.resourceId];
        }

        return isGroupedResource(props.resource)
            ? [props.resource.id, ...new Set([...props.resource.groupSubResources.map((_) => _.id)])]
            : [props.resource.id];
    }, [props]);
    const { isLoading, referenceCounts } = useResourceReferenceCounts({
        resourceIds: resourceIds,
        medicalProfileId: props.medicalProfileId,
        isDisabled: props.isLoadDisabled,
    });

    return <>{props.children({ isLoading, referenceCounts })}</>;
};

type ViewportAwareContainerProps = LazyLoadedReferenceCountsProps & {
    children: DataContainerChildren;
    forceRender?: boolean;
};

const ViewportAwareContainer = (props: ViewportAwareContainerProps) => {
    return (
        <IsInViewport>
            {({ isVisible }) => {
                return (
                    <DataContainer {...props} isLoadDisabled={!isVisible}>
                        {props.children}
                    </DataContainer>
                );
            }}
        </IsInViewport>
    );
};

const LazyLoadedReferenceCounts: React.FC<LazyLoadedReferenceCountsProps> = (props) => {
    return (
        <ViewportAwareContainer {...props}>
            {({ isLoading, referenceCounts }) => {
                return !isLoading ? <Card.CardReferenceCounts reference={referenceCounts} /> : null;
            }}
        </ViewportAwareContainer>
    );
};

export default React.memo(LazyLoadedReferenceCounts);

export type LazyLoadedPhysicianProps = LazyLoadedReferenceCountsProps & {
    children: (physician: string | null) => React.ReactNode;
};

export const LazyLoadedPhysician: React.FC<LazyLoadedPhysicianProps> = (props) => {
    return (
        <ViewportAwareContainer {...props}>
            {({ isLoading, referenceCounts }) => (
                <SkeletonLoadingContainer isLoading={isLoading}>
                    {props.children(referenceCounts.physician)}
                </SkeletonLoadingContainer>
            )}
        </ViewportAwareContainer>
    );
};
